import { Button, Close, Dropdown, Form, Input, Label, Modal } from '@bphxd/ds-core-react';
import styles from './CertificateMonitoring.module.scss';
import { useState } from 'react';
import { TCertificate } from '../../../lib/types';
import {
  GET_ALL_CERTIFICATES_WITH_JOINT_DETAILS,
  GET_CERTIFICATE_DETAILS,
  UPDATE_CERTIFICATE,
} from '../../../graph-queries/tar/certificate';
import { useMutation, useQuery } from '@apollo/client';
import { useNoticeBar } from '../../molecules/NoticeBar/NoticeBar';
import { ActionColumn, isAllComplete } from './CertificateMonitoring.util';
import { Kebab24, Remove24 } from '@bphxd/ds-core-react/lib/icons';
import { ModalHeader } from '@bphxd/ds-core-react/lib/components/modal/ModalHeader';
import { ModalBody } from '@bphxd/ds-core-react/lib/components/modal/ModalBody';
import { ModalFooter } from '@bphxd/ds-core-react/lib/components/modal/ModalFooter';

export const CertificateMonitoringActions: React.FC<{ certificateId: number }> = ({
  certificateId,
}) => {
  const { sendSuccess, sendError } = useNoticeBar();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => {
    setDropdownOpen((prevState) => !prevState);
  };

  const { data: certificateData } = useQuery(GET_CERTIFICATE_DETAILS, {
    variables: { certificateId: certificateId },
  });
  const certificate = certificateData?.certificateDetails as unknown as TCertificate;

  const [isAddCommentsModalOpen, setIsAddCommentsModalOpen] = useState(false);
  const [isViewCommentsModalOpen, setIsViewCommentsModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);

  const [comments, setComments] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const isAddCommentsButtonDisabled = certificate?.comments?.trim() === comments.trim();

  const allComplete = isAllComplete(certificate);

  const [updateCertificate] = useMutation(UPDATE_CERTIFICATE, {
    refetchQueries: [
      {
        query: GET_CERTIFICATE_DETAILS,
        variables: { certificateId: certificateId },
      },
      {
        query: GET_ALL_CERTIFICATES_WITH_JOINT_DETAILS,
        variables: { assetId: certificate?.assetId },
      },
    ],
    awaitRefetchQueries: true,
  });

  const handleSubmit = () => {
    setIsSubmitting(true);

    updateCertificate({
      variables: {
        input: {
          certificateId: certificate.certificateId,
          assetId: certificate.assetId,
          title: certificate.title,
          type: certificate.type,
          certificateNumber: certificate.certificateNumber,
          template: certificate.template,
          comments: comments.trim(),
        },
      },
    })
      .then(() => {
        sendSuccess(
          `Comments for certificate ${certificate.title} were added/updated successfully`,
          3000
        );
        setIsAddCommentsModalOpen(false);
        setIsSubmitting(false);
      })
      .catch((error) => {
        console.error('Error updating comments for certificate:', error);
        sendError(`Error updating comments for certificate ${certificate.title}`, 3000);
        setIsSubmitting(false);
        setIsAddCommentsModalOpen(false);
      });
  };

  return (
    <>
      <Dropdown isOpen={dropdownOpen} onToggleOpen={toggle} direction="end">
        <Dropdown.Toggle iconOnly Icon={dropdownOpen ? Remove24 : Kebab24} color="link" />
        <Dropdown.Menu className={styles.dropdownMenu}>
          <Dropdown.Item
            className={styles.dropdownItem}
            onClick={() => {
              if (!allComplete) return;
              setIsCompleteModalOpen(true);
            }}
            disabled={!allComplete}
          >
            Complete
          </Dropdown.Item>
          <Dropdown.Item
            className={styles.dropdownItem}
            onClick={() => {
              setComments(certificate?.comments || '');
              setIsAddCommentsModalOpen(true);
            }}
          >
            Add Comments
          </Dropdown.Item>
          <Dropdown.Item
            className={styles.dropdownItem}
            onClick={() => setIsViewCommentsModalOpen(true)}
          >
            View Comments
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>

      {/* complete modal */}
      {isCompleteModalOpen && (
        <Modal isOpen={isCompleteModalOpen} onToggleOpen={() => setIsCompleteModalOpen(false)}>
          <ModalHeader close={<Close onClick={() => setIsCompleteModalOpen(false)} />}>
            Complete Certificate &quot;{certificate.title}&quot;
          </ModalHeader>
          <ModalBody className={styles.completeModalBody}>
            <ActionColumn certificate={certificate} />
          </ModalBody>
          <ModalFooter>
            <Button level="secondary" onClick={() => setIsCompleteModalOpen(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Add comments modal */}
      {isAddCommentsModalOpen && (
        <Modal
          isOpen={isAddCommentsModalOpen}
          onToggleOpen={() => setIsAddCommentsModalOpen(false)}
        >
          <ModalHeader close={<Close onClick={() => setIsAddCommentsModalOpen(false)} />}>
            Add Comments for &quot;{certificate.title}&quot;
          </ModalHeader>
          <ModalBody>
            <Form>
              <Label for="comments">Comments</Label>
              <Input
                value={comments}
                onChange={(e) => setComments((e.target as HTMLTextAreaElement).value)}
                placeholder="Add comments"
                type="textarea"
              />
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button level="secondary" onClick={() => setIsAddCommentsModalOpen(false)}>
              Cancel
            </Button>
            <Button
              level="primary"
              onClick={handleSubmit}
              disabled={
                isAddCommentsButtonDisabled ||
                (!(certificate.comments && certificate.comments.trim()) &&
                  comments.trim() === '') ||
                isSubmitting
              }
            >
              {certificate.comments && certificate.comments.trim()
                ? 'Update comments'
                : 'Add Comments'}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* View comments modal */}
      {isViewCommentsModalOpen && (
        <Modal
          isOpen={isViewCommentsModalOpen}
          onToggleOpen={() => setIsViewCommentsModalOpen(false)}
        >
          <ModalHeader close={<Close onClick={() => setIsViewCommentsModalOpen(false)} />}>
            View Comments for &quot;{certificate.title}&quot;
          </ModalHeader>
          <ModalBody>
            {certificate.comments ? (
              <p className={styles.disabledText}>{certificate.comments}</p>
            ) : (
              <p className={styles.noComments}>No comments available</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button level="secondary" onClick={() => setIsViewCommentsModalOpen(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};
